/**
 * Every `useMutation` must declare which surface owns telling the user about a
 * rejection: the shared global toast, or the page's own inline UI (Alert,
 * per-field message, isError-driven banner). Without a forced declaration a
 * hook silently defaults to whichever the last refactor left behind (AB#1749:
 * a mutation with both an inline Alert AND the global toast showed the same
 * sentence twice; the previous guard read a totally unrelated signal —
 * `mutation.options.onError` — that never lined up with how the app actually
 * reports failures).
 *
 * `mutationErrorChannelGuard.test.ts` fails the build for any `useMutation(`
 * call site whose options object doesn't carry one of these under `meta`.
 */
export const MutationErrorChannel = {
  /** Global `sonner` toast shows the error. Default choice for actions with no dedicated failure UI (deletes, background writes, dialog actions without an inline slot). */
  Toast: 'errorToast',
  /** The calling component owns rendering the failure (inline `Alert`, per-field error, `isError`-driven banner). The shared `MutationCache` handler must not also toast. */
  Inline: 'errorInline',
} as const;

export type MutationErrorChannelMeta =
  | { errorToast: true; errorInline?: never }
  | { errorInline: true; errorToast?: never };

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: MutationErrorChannelMeta;
  }
}
